import { ShieldCheck } from "lucide-react";
import { Seo } from "@/components/Seo";
import { Overline, Reveal } from "@/components/Reveal";
import { CtaBand } from "@/components/CtaBand";
import { COMPANY, COMPLIANCE_DISCLAIMER } from "@/data/site";

const items = [
    { title: "GST Registration", detail: `GST ${COMPANY.gst} — Verified & Active` },
    { title: "Import Export Code (IEC)", detail: `IEC ${COMPANY.iec} — Verified & Active` },
    { title: "LLP Registration", detail: "Limited Liability Partnership — Verified & Active" },
    { title: "IOPEPC / APEDA RCMC", detail: "Registration-cum-Membership Certificate — Verified & Active where applicable" },
    { title: "AD Code & Bank Export Setup", detail: "Authorized Dealer code and export banking — Verified & Active" },
    { title: "Certificate of Origin", detail: "CoO support arranged for every shipment per destination requirements" },
    { title: "Batch-wise Certificate of Analysis", detail: "COA coordinated for every batch, with lab test support on request" },
    { title: "Third-party Inspection", detail: "SGS, Geo-Chem, or equivalent inspection support available on request" },
    { title: "Phytosanitary & Fumigation", detail: "Documents arranged per destination-country import rules" },
    { title: "Partner Facility Standards", detail: "ISO 22000 / HACCP / GMP+ certified partner facilities where available" },
];

export default function Compliance() {
    return (
        <>
            <Seo
                title="Compliance & Documentation — GST, IEC, RCMC | AXORA PS GLOBAL LLP"
                description="AXORA PS GLOBAL LLP compliance credentials: GST 08ACOFA1315L1ZB, IEC ACOFA1315L, LLP registration, RCMC, AD code, certificate of origin, batch-wise COA, and third-party inspection support."
                path="/compliance"
                ogImage="/images/og-compliance.jpg"
            />

            <section data-testid="compliance-hero" className="relative overflow-hidden bg-white pt-[72px]">
                <div aria-hidden="true" className="pointer-events-none absolute inset-x-0 top-0 h-[420px] bg-gradient-to-b from-steel-light/80 via-steel-light/30 to-white" />
                <div className="relative mx-auto w-full max-w-7xl px-5 pb-16 pt-14 sm:px-8 lg:px-12 lg:pb-20 lg:pt-20">
                    <Reveal>
                        <Overline>Compliance</Overline>
                        <h1 className="mt-4 max-w-3xl font-display text-4xl font-bold leading-[1.08] tracking-tight text-navy sm:text-5xl">
                            Compliance & Documentation
                        </h1>
                        <p className="mt-6 max-w-2xl text-base leading-relaxed text-slate-600 md:text-lg">
                            Export paperwork is where most commodity deals slow down. Our registrations, banking, and
                            documentation processes are set up so your cargo clears without surprises.
                        </p>
                    </Reveal>
                </div>
            </section>

            <section data-testid="compliance-grid" className="border-t border-slate-200 bg-white py-20 lg:py-28">
                <div className="mx-auto w-full max-w-7xl px-5 sm:px-8 lg:px-12">
                    <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
                        {items.map((item, i) => (
                            <Reveal key={item.title} delay={i * 0.05}>
                                <div
                                    data-testid={`compliance-item-${item.title.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                                    className="flex h-full items-start gap-4 rounded-xl border border-slate-200 bg-white p-6 transition-all duration-300 hover:-translate-y-1 hover:border-steel/50 hover:shadow-[0_16px_40px_rgba(10,37,64,0.1)]"
                                >
                                    <ShieldCheck className="mt-0.5 h-6 w-6 shrink-0 text-amber" />
                                    <div>
                                        <h2 className="font-display text-base font-bold tracking-tight text-navy">{item.title}</h2>
                                        <p className="mt-1.5 text-sm leading-relaxed text-slate-600">{item.detail}</p>
                                    </div>
                                </div>
                            </Reveal>
                        ))}
                    </div>

                    <Reveal delay={0.08} className="mt-14">
                        <img
                            src="/images/lab-testing.jpg"
                            alt="Laboratory bench equipped for oilseed meal quality testing, COA verification, and batch analysis"
                            loading="lazy"
                            className="aspect-[21/9] w-full rounded-2xl border border-slate-200 object-cover"
                        />
                    </Reveal>

                    <Reveal delay={0.1} className="mt-14">
                        <div
                            data-testid="compliance-disclaimer"
                            role="note"
                            className="rounded-2xl border-l-4 border-amber bg-amber/5 p-7 sm:p-9"
                        >
                            <h2 className="font-display text-lg font-bold tracking-tight text-navy">Important Notice for Buyers</h2>
                            <p className="mt-3 max-w-4xl text-sm leading-relaxed text-slate-700 md:text-base">
                                {COMPLIANCE_DISCLAIMER}
                            </p>
                        </div>
                    </Reveal>
                </div>
            </section>

            <CtaBand
                location="compliance"
                title="Need Compliance Documents Before You Commit?"
                body="Request our registration details, a sample COA, or third-party inspection arrangements directly on WhatsApp — we share documentation with serious buyers before contract."
                label="Connect on WhatsApp"
            />
        </>
    );
}
